import { createClient } from '@supabase/supabase-js';
import { logger, LogCategory } from './logger';

// Supabase URL and anon key from environment variables
const supabaseUrl = process.env.VUE_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.VUE_APP_SUPABASE_ANON_KEY || '';
const supabaseServiceKey = process.env.VUE_APP_SUPABASE_SERVICE_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyAgCiAgICAicm9sZSI6ICJzZXJ2aWNlX3JvbGUiLAogICAgImlzcyI6ICJzdXBhYmFzZS1kZW1vIiwKICAgICJpYXQiOiAxNjQxNzY5MjAwLAogICAgImV4cCI6IDE3OTk1MzU2MDAKfQ.DaYlNEoUrrEn2Ig7tqibS-PHK5vgusbcbo7X36XVt4Q';

// Log environment variable status
logger.info('Environment variables loaded', LogCategory.GENERAL, {
  supabaseUrl: supabaseUrl ? 'Available' : 'Missing',
  supabaseAnonKey: supabaseAnonKey ? 'Available' : 'Missing',
  supabaseServiceKey: supabaseServiceKey ? 'Available' : 'Missing'
});

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  const errorMsg = 'Missing required Supabase environment variables: VUE_APP_SUPABASE_URL and VUE_APP_SUPABASE_ANON_KEY must be set in .env file.';
  logger.error(errorMsg, LogCategory.SECURITY);
  throw new Error(errorMsg);
}

// Storage key for auth session
const storageKey = process.env.VUE_APP_SUPABASE_STORAGE_KEY || 'fraport-supabase-auth';

// Create a regular client for user operations
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey,
    // Use cookies instead of localStorage
    storage: {
      getItem: (key) => {
        const value = document.cookie
          .split('; ')
          .find(row => row.startsWith(`${key}=`))
          ?.split('=')[1];
        return value ? decodeURIComponent(value) : null;
      },
      setItem: (key, value) => {
        const maxAge = 60 * 60 * 24 * 7; // 7 days
        const secure = process.env.NODE_ENV === 'production' ? 'Secure;' : '';
        document.cookie = `${key}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax;${secure}`;
      },
      removeItem: (key) => {
        logger.debug('Removing cookie', { key });
        // Set expiration in the past to remove the cookie
        document.cookie = `${key}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT;SameSite=Lax;`;
      }
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    },
    // Increase heartbeat interval and timeout for better connection stability
    heartbeatIntervalMs: 1000,  // More frequent heartbeats (1 second)
    timeout: 60000  // Longer timeout (60 seconds)
  },
  // Global fetch options to increase timeouts
  global: {
    fetch: (url, options) => {
      // Set a longer timeout for all requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      const fetchPromise = fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      // Clear the timeout when the fetch completes
      fetchPromise.finally(() => clearTimeout(timeoutId));
      
      return fetchPromise;
    }
  }
});

// Create admin client with service role key (if available)
export const supabaseAdmin = supabaseServiceKey
  ? createClient(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    })
  : null;

// Function to get appropriate client based on role
export function getSupabaseClient(role: 'anon' | 'service' = 'anon') {
  if (role === 'service' && supabaseAdmin) {
    return supabaseAdmin;
  }
  return supabase;
}

// Log connection attempts
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN') {
    logger.info('User signed in', LogCategory.AUTH, { userId: session?.user?.id });
  } else if (event === 'SIGNED_OUT') {
    logger.info('User signed out', LogCategory.AUTH);
  } else if (event === 'TOKEN_REFRESHED') {
    logger.debug('Auth token refreshed', LogCategory.AUTH);
  }
});

// Test and log connection status
setTimeout(async () => {
  try {
    const { error } = await supabase.from('profiles').select('count').limit(1);
    if (error) {
      logger.error('Failed to establish Supabase connection', LogCategory.DATABASE, { error });
    } else {
      logger.info('Supabase connection established', LogCategory.DATABASE, { timestamp: new Date().toISOString() });
    }
  } catch (err) {
    logger.error('Failed to establish Supabase connection', LogCategory.DATABASE, { error: err });
  }
}, 1000); // Delay the test to ensure the client is fully initialized

// Set up a periodic heartbeat to keep the connection alive
if (typeof window !== 'undefined') {
  let heartbeatCount = 0;
  let heartbeatInterval: number | null = null;
  const ACTIVE_HEARTBEAT_INTERVAL = 30000; // 30 seconds when tab is active
  const HIDDEN_HEARTBEAT_INTERVAL = 5000;  // 5 seconds when tab is hidden (more aggressive)
  let isTabHidden = false;
  
  // Function to perform a lightweight heartbeat
  const performHeartbeat = async () => {
    try {
      heartbeatCount++;
      
      // Only log occasionally to avoid console spam
      const shouldLog = heartbeatCount % 20 === 0 || isTabHidden; // Log every 20th heartbeat or when tab is hidden
      
      if (shouldLog) {
        console.log(`[HEARTBEAT] Performing heartbeat #${heartbeatCount} at ${new Date().toISOString()} (tab ${isTabHidden ? 'hidden' : 'visible'})`);
      }
      
      // Perform a lightweight query to keep the connection alive
      const startTime = performance.now();
      const { error } = await supabase.from('profiles').select('count').limit(1);
      const duration = performance.now() - startTime;
      
      if (error) {
        console.error('[HEARTBEAT] Error during heartbeat:', error);
        logger.warn('Heartbeat query failed', LogCategory.DATABASE, { error });
      } else if (shouldLog) {
        console.log(`[HEARTBEAT] Heartbeat successful (${Math.round(duration)}ms)`);
      }
    } catch (err) {
      console.error('[HEARTBEAT] Exception during heartbeat:', err);
      logger.error('Heartbeat exception', LogCategory.DATABASE, { error: err });
    }
  };
  
  // Function to start the heartbeat with the specified interval
  const startHeartbeat = (interval: number) => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }
    
    heartbeatInterval = window.setInterval(performHeartbeat, interval);
    console.log(`[HEARTBEAT] Heartbeat ${isTabHidden ? 'hidden' : 'visible'} mode started with interval: ${interval}ms`);
  };
  
  // Listen for visibility changes to adjust heartbeat frequency
  document.addEventListener('visibilitychange', () => {
    isTabHidden = document.visibilityState === 'hidden';
    
    if (isTabHidden) {
      // Tab is hidden, increase heartbeat frequency
      console.log('[HEARTBEAT] Tab hidden, increasing heartbeat frequency');
      startHeartbeat(HIDDEN_HEARTBEAT_INTERVAL);
    } else {
      // Tab is visible, decrease heartbeat frequency
      console.log('[HEARTBEAT] Tab visible, decreasing heartbeat frequency');
      startHeartbeat(ACTIVE_HEARTBEAT_INTERVAL);
    }
  });
  
  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  });
  
  // Start the initial heartbeat
  startHeartbeat(ACTIVE_HEARTBEAT_INTERVAL);
  
  // Perform an immediate heartbeat
  setTimeout(performHeartbeat, 1000);
  
  console.log('[HEARTBEAT] Adaptive heartbeat system initialized');
}

/**
 * COMPLETE RESET: Reinitializes everything related to Supabase and backend connections
 * This is the most comprehensive approach possible without reloading the page
 */
export async function emergencyResetSupabase() {
  console.log('%c[EMERGENCY] COMPLETE RESET: Reinitializing all Supabase and backend connections', 'background: #ff0000; color: white; font-size: 14px; font-weight: bold; padding: 4px;');
  console.log('[EMERGENCY] Current time:', new Date().toISOString());
  console.log('[EMERGENCY] Current Supabase client state:', {
    auth: supabase.auth ? 'Available' : 'Missing',
    realtime: supabase.realtime ? 'Available' : 'Missing'
  });
  logger.info('Complete reset of all Supabase and backend connections', LogCategory.DATABASE);
  
  // Force a small delay to ensure any pending operations complete
  await new Promise(resolve => setTimeout(resolve, 100));
  
  // Store the current session before reset
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let currentSession: any = null;
  try {
    const { data } = await supabase.auth.getSession();
    currentSession = data.session;
    console.log('[EMERGENCY] Stored current session before reset:', currentSession ? 'Available' : 'Not available');
  } catch (sessionErr) {
    console.error('[EMERGENCY] Error getting current session before reset:', sessionErr);
  }
  
  try {
    // Instead of creating a completely new client, we'll modify the existing one
    console.log('[EMERGENCY] Modifying existing client instead of creating a new one');
    
    // 1. Force a reconnection of the realtime connection
    if (supabase.realtime) {
      try {
        console.log('[EMERGENCY] Forcing reconnection of realtime');
        // Access internal methods using type assertion
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const realtime = supabase.realtime as any;
        if (typeof realtime.disconnect === 'function') {
          await realtime.disconnect();
        }
        if (typeof realtime.connect === 'function') {
          await realtime.connect();
        }
      } catch (realtimeErr) {
        console.error('[EMERGENCY] Error reconnecting realtime:', realtimeErr);
      }
    }
    
    // 2. Create a temporary client just for auth operations
    const tempClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: false, // Don't persist to avoid conflicts
        autoRefreshToken: false, // We'll handle this manually
        detectSessionInUrl: false
      }
    });
    
    console.log('[EMERGENCY] Temporary client created successfully');
    
    // We'll use the existing client for most operations
    const newClient = supabase;
    
    // 2. HACK: Replace the global supabase object with the new client
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Object.assign(supabase as any, newClient);
      console.log('[EMERGENCY] Successfully replaced global supabase client');
    } catch (replaceErr) {
      console.error('[EMERGENCY] Failed to replace global client:', replaceErr);
    }
    
    // 3. Reinitialize auth using the stored session
    try {
      console.log('[EMERGENCY] Reinitializing auth state');
      
      if (currentSession) {
        console.log('[EMERGENCY] Using stored session to reinitialize auth');
        
        try {
          // Extract the required tokens from the stored session
          const { access_token, refresh_token } = currentSession;
          
          // First, try to set the session directly
          const setSessionResult = await tempClient.auth.setSession({
            access_token,
            refresh_token
          });
          
          if (setSessionResult.error) {
            console.error('[EMERGENCY] Error setting session from stored session:', setSessionResult.error);
          } else {
            console.log('[EMERGENCY] Session set successfully from stored session');
            
            // Now set the session on the main client
            // Check if session exists to satisfy TypeScript
            if (setSessionResult.data.session) {
              try {
                // Set the session on the main client
                const result = await supabase.auth.setSession({
                  access_token: setSessionResult.data.session.access_token,
                  refresh_token: setSessionResult.data.session.refresh_token
                });
                
                // Check for errors
                if (result.error) {
                  console.error('[EMERGENCY] Error setting session on main client:', result.error);
                } else {
                  console.log('[EMERGENCY] Successfully set session on main client');
                }
              } catch (mainSetSessionErr) {
                console.error('[EMERGENCY] Exception setting session on main client:', mainSetSessionErr);
              }
            } else {
              console.error('[EMERGENCY] No session in setSessionResult');
            }
          }
        } catch (setSessionErr) {
          console.error('[EMERGENCY] Error setting session from stored session:', setSessionErr);
          
          // Fallback: try to refresh the session
          try {
            console.log('[EMERGENCY] Falling back to session refresh');
            const refreshResult = await supabase.auth.refreshSession();
            
            if (refreshResult.error) {
              console.error('[EMERGENCY] Error refreshing session:', refreshResult.error);
            } else {
              console.log('[EMERGENCY] Auth token refreshed successfully');
            }
          } catch (refreshErr) {
            console.error('[EMERGENCY] Error in refresh fallback:', refreshErr);
          }
        }
      } else {
        console.log('[EMERGENCY] No stored session found, trying to get current session');
        
        // Try to get the current session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[EMERGENCY] Error getting current session:', error);
        } else if (data.session) {
          console.log('[EMERGENCY] Current session found, refreshing token');
          
          // Force refresh the session
          const refreshResult = await supabase.auth.refreshSession();
          
          if (refreshResult.error) {
            console.error('[EMERGENCY] Error refreshing current session:', refreshResult.error);
          } else {
            console.log('[EMERGENCY] Current session refreshed successfully');
          }
        } else {
          console.log('[EMERGENCY] No session found, auth may need to be reinitialized manually');
        }
      }
    } catch (authErr) {
      console.error('[EMERGENCY] Error reinitializing auth:', authErr);
    }
    
    // 3.5. Attempt to reconnect realtime channels
    try {
      console.log('[EMERGENCY] Attempting to reconnect realtime channels');
      
      // Get current channels
      const channels = newClient.getChannels ? newClient.getChannels() : [];
      console.log('[EMERGENCY] Found', channels.length, 'realtime channels');
      
      // Reconnect each channel
      for (const channel of channels) {
        try {
          console.log('[EMERGENCY] Reconnecting channel:', channel.topic);
          
          // Unsubscribe and resubscribe to force a fresh connection
          channel.unsubscribe();
          setTimeout(() => {
            try {
              channel.subscribe();
              console.log('[EMERGENCY] Successfully reconnected channel:', channel.topic);
            } catch (subErr) {
              console.error('[EMERGENCY] Error resubscribing to channel:', channel.topic, subErr);
            }
          }, 500); // Small delay before resubscribing
        } catch (channelErr) {
          console.error('[EMERGENCY] Error reconnecting channel:', channel.topic, channelErr);
        }
      }
    } catch (realtimeErr) {
      console.error('[EMERGENCY] Error reconnecting realtime channels:', realtimeErr);
    }
    
    // 4. Force a reload of the current user
    try {
      console.log('[EMERGENCY] Forcing reload of current user');
      const { data: userData, error: userError } = await newClient.auth.getUser();
      
      if (userError) {
        console.error('[EMERGENCY] Error getting current user:', userError);
      } else if (userData.user) {
        console.log('[EMERGENCY] Current user reloaded successfully:', userData.user.id);
      } else {
        console.warn('[EMERGENCY] No current user found');
      }
    } catch (userErr) {
      console.error('[EMERGENCY] Error reloading current user:', userErr);
    }
    
    // 5. Force a reload of all profiles data
    try {
      console.log('[EMERGENCY] Forcing reload of profiles data');
      const { data: profileData, error: profileError } = await newClient.from('profiles').select('*').limit(10);
      
      if (profileError) {
        console.error('[EMERGENCY] Error reloading profiles data:', profileError);
      } else {
        console.log('[EMERGENCY] Profiles data reloaded successfully, count:', profileData.length);
      }
    } catch (profileErr) {
      console.error('[EMERGENCY] Error reloading profiles data:', profileErr);
    }
    
    // 6. Dispatch a series of events to notify all parts of the application
    if (typeof window !== 'undefined') {
      // First notify about the auth reinitialization
      console.log('[EMERGENCY] Dispatching auth reinitialization event');
      const authEvent = new CustomEvent('supabase-auth-reinitialized');
      window.dispatchEvent(authEvent);
      
      // Then notify about database reconnection
      console.log('[EMERGENCY] Dispatching database reconnection event');
      const dbEvent = new CustomEvent('database-reconnected');
      window.dispatchEvent(dbEvent);
      
      // Then notify about general Supabase reconnection
      console.log('[EMERGENCY] Dispatching general Supabase reconnection event');
      const reconnectEvent = new CustomEvent('supabase-reconnected');
      window.dispatchEvent(reconnectEvent);
      
      // Dispatch auth-refreshed event
      console.log('[EMERGENCY] Dispatching auth-refreshed event');
      const authRefreshedEvent = new CustomEvent('auth-refreshed', {
        detail: {
          timestamp: new Date().toISOString(),
          source: 'emergency-reset'
        }
      });
      window.dispatchEvent(authRefreshedEvent);
      
      // Dispatch connection-status event
      console.log('[EMERGENCY] Dispatching connection-status event');
      const statusEvent = new CustomEvent('connection-status', {
        detail: {
          status: 'connected',
          timestamp: new Date().toISOString(),
          source: 'emergency-reset'
        }
      });
      window.dispatchEvent(statusEvent);
      
      // Finally dispatch a special "complete reset" event
      console.log('[EMERGENCY] Dispatching complete reset event');
      const completeResetEvent = new CustomEvent('supabase-complete-reset');
      window.dispatchEvent(completeResetEvent);
      
      // Force a small delay to ensure events are processed
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // 5. Log success message (but don't show alert to user)
    console.log('[EMERGENCY] Complete reset successful');
    logger.info('Complete reset of Supabase connections successful', LogCategory.DATABASE);
    
    // Return the new client
    return newClient;
  } catch (err) {
    console.error('[EMERGENCY] Error during complete reset:', err);
    logger.error('Failed to perform complete reset', LogCategory.DATABASE, { error: err });
    
    // Log error message (but don't show alert to user)
    console.error('[EMERGENCY] Complete reset failed');
    logger.error('Complete reset of Supabase connections failed', LogCategory.DATABASE, { error: err });
    
    return null;
  }
}

// Add aggressive visibility change handler to immediately reconnect Supabase when tab becomes visible again
if (typeof document !== 'undefined') {
  // Track tab state
  let lastHiddenTime = 0;
  let visibilityChangeCount = 0;
  
  document.addEventListener('visibilitychange', async () => {
    visibilityChangeCount++;
    
    if (document.visibilityState === 'hidden') {
      // Tab is now hidden
      lastHiddenTime = Date.now();
      console.log('%c[VISIBILITY] Tab hidden', 'background: #0000ff; color: white; font-size: 14px; font-weight: bold; padding: 4px;');
      console.log('[VISIBILITY] Tab hidden at', new Date().toISOString());
      console.log('[VISIBILITY] Change count:', visibilityChangeCount);
      logger.info('Tab hidden', LogCategory.DATABASE);
    } else if (document.visibilityState === 'visible') {
      const hiddenDuration = Date.now() - lastHiddenTime;
      console.log('%c[VISIBILITY] Tab visible again', 'background: #00ff00; color: black; font-size: 14px; font-weight: bold; padding: 4px;');
      console.log('[VISIBILITY] Tab visible again at', new Date().toISOString());
      console.log('[VISIBILITY] Tab was hidden for', Math.round(hiddenDuration/1000), 'seconds');
      console.log('[VISIBILITY] Change count:', visibilityChangeCount);
      
      // Log the current state
      console.log('[VISIBILITY] Current Supabase client state:', {
        auth: supabase.auth ? 'Available' : 'Missing',
        realtime: supabase.realtime ? 'Available' : 'Missing',
        channels: supabase.getChannels ? supabase.getChannels().length : 'Unknown'
      });
      
      logger.info('Tab visible again, refreshing session and connections', LogCategory.DATABASE);
      
      // ALWAYS perform a complete reset when the tab becomes visible again
      // This is the most aggressive approach to ensure the connection is always fresh
      console.log('[VISIBILITY] Tab visible again, performing complete reset');
      logger.info('Tab visible again, performing complete reset', LogCategory.DATABASE);
      
      try {
        // Force a small delay before reset
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // First, try to get the current session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('[VISIBILITY] Error getting current session:', sessionError);
        } else if (sessionData.session) {
          console.log('[VISIBILITY] Current session found, refreshing token');
          
          // Force refresh the session first
          const refreshResult = await supabase.auth.refreshSession();
          
          if (refreshResult.error) {
            console.error('[VISIBILITY] Error refreshing session:', refreshResult.error);
          } else {
            console.log('[VISIBILITY] Session refreshed successfully');
          }
        }
        
        // Use the emergency reset function to completely reinitialize everything
        await emergencyResetSupabase();
        
        // Force another small delay after reset
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Force a reload of the current user after reset
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error('[VISIBILITY] Error getting current user after reset:', userError);
          
          // If we can't get the user, try to refresh the session again
          const refreshResult = await supabase.auth.refreshSession();
          
          if (refreshResult.error) {
            console.error('[VISIBILITY] Error refreshing session after user error:', refreshResult.error);
          } else {
            console.log('[VISIBILITY] Session refreshed successfully after user error');
            
            // Try to get the user again
            const retryUserResult = await supabase.auth.getUser();
            if (retryUserResult.error) {
              console.error('[VISIBILITY] Error getting user after session refresh:', retryUserResult.error);
            } else if (retryUserResult.data.user) {
              console.log('[VISIBILITY] User retrieved successfully after session refresh:', retryUserResult.data.user.id);
            }
          }
        } else if (userData.user) {
          console.log('[VISIBILITY] Current user reloaded successfully after reset:', userData.user.id);
          
          // Force a reload of the user's profile
          try {
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', userData.user.id)
              .single();
              
            if (profileError) {
              console.error('[VISIBILITY] Error loading user profile after reset:', profileError);
            } else if (profileData) {
              console.log('[VISIBILITY] User profile reloaded successfully after reset');
            }
          } catch (profileErr) {
            console.error('[VISIBILITY] Error loading user profile after reset:', profileErr);
          }
        }
        
        // Force a reload of all profiles data to ensure database connection is working
        try {
          console.log('[VISIBILITY] Forcing reload of profiles data');
          const { error: profilesError } = await supabase.from('profiles').select('count').limit(1);
          
          if (profilesError) {
            console.error('[VISIBILITY] Error reloading profiles data:', profilesError);
          } else {
            console.log('[VISIBILITY] Profiles data reloaded successfully');
          }
        } catch (profilesErr) {
          console.error('[VISIBILITY] Error reloading profiles data:', profilesErr);
        }
        
        // Dispatch tab-visible event after reset
        if (typeof window !== 'undefined') {
          // Force a small delay before dispatching events
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Dispatch tab-visible event
          console.log('[VISIBILITY] Dispatching tab-visible event');
          const visibleEvent = new CustomEvent('tab-visible');
          window.dispatchEvent(visibleEvent);
          
          // Force a small delay between events
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Dispatch a special event to force all components to refresh
          console.log('[VISIBILITY] Dispatching force-refresh event');
          const forceRefreshEvent = new CustomEvent('force-refresh', {
            detail: {
              timestamp: new Date().toISOString(),
              source: 'visibility-change'
            }
          });
          window.dispatchEvent(forceRefreshEvent);
          
          // Force a small delay between events
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Dispatch a special event to force all auth-dependent components to refresh
          console.log('[VISIBILITY] Dispatching force-auth-refresh event');
          const forceAuthRefreshEvent = new CustomEvent('force-auth-refresh', {
            detail: {
              timestamp: new Date().toISOString(),
              source: 'visibility-change',
              userId: userData?.user?.id
            }
          });
          window.dispatchEvent(forceAuthRefreshEvent);
          
          // Force a small delay between events
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Dispatch a super-aggressive-reconnect event
          console.log('[VISIBILITY] Dispatching super-aggressive-reconnect event');
          const superReconnectEvent = new CustomEvent('super-aggressive-reconnect', {
            detail: {
              timestamp: new Date().toISOString(),
              source: 'visibility-change'
            }
          });
          window.dispatchEvent(superReconnectEvent);
        }
      } catch (err) {
        console.error('[VISIBILITY] Error during emergency reset:', err);
        logger.error('Error during emergency reset on tab visibility change', LogCategory.DATABASE, { error: err });
        
        // Even if the reset fails, try to dispatch events to notify components
        if (typeof window !== 'undefined') {
          console.log('[VISIBILITY] Dispatching tab-visible event (after error)');
          const visibleEvent = new CustomEvent('tab-visible');
          window.dispatchEvent(visibleEvent);
          
          // Dispatch a super-aggressive-reconnect event
          console.log('[VISIBILITY] Dispatching super-aggressive-reconnect event (after error)');
          const superReconnectEvent = new CustomEvent('super-aggressive-reconnect', {
            detail: {
              timestamp: new Date().toISOString(),
              source: 'visibility-change-error'
            }
          });
          window.dispatchEvent(superReconnectEvent);
        }
      }
    }
  });
}

// Export types for better TypeScript integration
export type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Manually tests the Supabase connection and logs detailed results
 * This is useful for debugging connection issues
 */
export async function testSupabaseConnection(): Promise<{
  auth: { success: boolean; error: any; duration: number };
  query: { success: boolean; error: any; duration: number };
  storage: { success: boolean; error: any; duration: number };
  overall: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  unexpectedError?: any;
}> {
  console.log('%c[CONNECTION TEST] Starting comprehensive connection test', 'background: #ffff00; color: black; font-size: 14px; font-weight: bold; padding: 4px;');
  console.log('[CONNECTION TEST] Current time:', new Date().toISOString());
  
  // Test results object
  const results = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    auth: { success: false, error: null as any, duration: 0 },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query: { success: false, error: null as any, duration: 0 },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    storage: { success: false, error: null as any, duration: 0 },
    overall: false
  };
  
  try {
    // 1. Test auth connection
    console.log('[CONNECTION TEST] Step 1: Testing auth connection');
    console.time('Auth test');
    const authStart = performance.now();
    
    try {
      const { data, error } = await supabase.auth.getSession();
      results.auth.duration = performance.now() - authStart;
      
      if (error) {
        results.auth.error = error;
        console.error('[CONNECTION TEST] Auth test failed:', error);
      } else {
        results.auth.success = true;
        console.log('[CONNECTION TEST] Auth test successful:', {
          hasSession: !!data.session,
          expiresAt: data.session?.expires_at ? new Date(data.session.expires_at * 1000).toISOString() : 'N/A'
        });
      }
    } catch (authErr) {
      results.auth.error = authErr;
      results.auth.duration = performance.now() - authStart;
      console.error('[CONNECTION TEST] Auth test exception:', authErr);
    }
    console.timeEnd('Auth test');
    
    // 2. Test database query
    console.log('[CONNECTION TEST] Step 2: Testing database query');
    console.time('Query test');
    const queryStart = performance.now();
    
    try {
      const { data, error } = await supabase.from('profiles').select('count').limit(1);
      results.query.duration = performance.now() - queryStart;
      
      if (error) {
        results.query.error = error;
        console.error('[CONNECTION TEST] Query test failed:', error);
      } else {
        results.query.success = true;
        console.log('[CONNECTION TEST] Query test successful:', data);
      }
    } catch (queryErr) {
      results.query.error = queryErr;
      results.query.duration = performance.now() - queryStart;
      console.error('[CONNECTION TEST] Query test exception:', queryErr);
    }
    console.timeEnd('Query test');
    
    // 3. Test storage connection
    console.log('[CONNECTION TEST] Step 3: Testing storage connection');
    console.time('Storage test');
    const storageStart = performance.now();
    
    try {
      const { data, error } = await supabase.storage.listBuckets();
      results.storage.duration = performance.now() - storageStart;
      
      if (error) {
        results.storage.error = error;
        console.error('[CONNECTION TEST] Storage test failed:', error);
      } else {
        results.storage.success = true;
        console.log('[CONNECTION TEST] Storage test successful:', {
          bucketCount: data?.length || 0
        });
      }
    } catch (storageErr) {
      results.storage.error = storageErr;
      results.storage.duration = performance.now() - storageStart;
      console.error('[CONNECTION TEST] Storage test exception:', storageErr);
    }
    console.timeEnd('Storage test');
    
    // Determine overall success
    results.overall = results.auth.success && results.query.success;
    
    // Log summary
    console.log('%c[CONNECTION TEST] Test complete', 'background: #ffff00; color: black; font-size: 14px; font-weight: bold; padding: 4px;');
    console.log('[CONNECTION TEST] Results:', {
      overall: results.overall ? 'SUCCESS' : 'FAILURE',
      auth: `${results.auth.success ? 'SUCCESS' : 'FAILURE'} (${Math.round(results.auth.duration)}ms)`,
      query: `${results.query.success ? 'SUCCESS' : 'FAILURE'} (${Math.round(results.query.duration)}ms)`,
      storage: `${results.storage.success ? 'SUCCESS' : 'FAILURE'} (${Math.round(results.storage.duration)}ms)`
    });
    
    return results;
  } catch (err) {
    console.error('[CONNECTION TEST] Unexpected error during test:', err);
    return {
      ...results,
      unexpectedError: err
    };
  }
}
